import React, { FC, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTheme } from 'emotion-theming'
import { ALPHABET } from 'utils/constants/alphabet'
import flexCenter from 'utils/styles/flexCenter'
import Loader from 'components/common/atoms/Loader'

type AlphabetMenuProps = {
  activeAlphabet: string
  onClick: (char: string) => void
  disabledIndex: string[]
  kind?: 'default' | 'index'
  loading: boolean
  stickyProps?: {
    indexClassName: string
    startStickyClassName: string
    stopStickyClassName: string
    infiniteScroll: boolean
  }
  url?: string
}

const alphabetMenuStyle = css`
  @media screen and (max-width: 767px) {
    overflow-x: scroll;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  .alphabet-menu {
    &__wrapper {
      display: flex;
      justify-content: space-between;
    }
  }
`

const alphabetMenuIndexStyle = (theme: ThemeType) => css`
  @media screen and (max-width: 767px) {
    position: absolute;
    height: 607px;
    width: 44px;
    overflow-y: scroll;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  .alphabet-menu {
    @media screen and (min-width: 768px) {
      &__wrapper {
        background-color: ${theme.neutral.white};
        height: 40px;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        padding: 0 16px;
        border-radius: 10px;
        box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.3);
      }
    }
  }
`

const charStyle = (theme: ThemeType) => css`
  padding: 8px;
  cursor: pointer;
  font-size: 12px;
  color: ${theme.gray.gray70};
  &.disabled {
    color: ${theme.gray.gray30};
    cursor: not-allowed;
  }
  &:first-of-type {
    margin-left: 16px;
  }
  &:last-of-type {
    padding-right: 16px;
  }
  @media screen and (min-width: 768px) {
    padding: 8px 16px;
    font-size: 16px;
    &:first-of-type {
      margin-left: 0;
    }
    &:last-of-type {
      padding-right: 0;
    }
  }
`

const charIndexStyle = (theme: ThemeType) => css`
  background-color: ${theme.gray.gray20};
  color: ${theme.gray.gray40};
  font-weight: bold;
  ${flexCenter};
  width: 44px;
  height: 37px;
  border-bottom: 1px solid ${theme.neutral.white};
  &.active {
    color: ${theme.gray.gray70};
    background-color: ${theme.neutral.white};
  }
  &.disabled {
    color: ${theme.gray.gray30};
  }
  @media screen and (min-width: 768px) {
    color: ${theme.gray.gray70};
    background-color: ${theme.neutral.white};
    flex-basis: 50%;
    height: inherit;
    width: auto;
    cursor: pointer;
    border-bottom: none;
    font-weight: normal;
    &.active,
    &:hover {
      background-color: ${theme.campus.aqua10};
      color: ${theme.campus.aqua30};
    }
    &.disabled {
      cursor: not-allowed;
      color: ${theme.gray.gray30};
      background-color: ${theme.neutral.white};
    }
  }
`

const stickyAlphabetMenuStyle = css`
  @media screen and (max-width: 767px) {
    position: fixed !important;
    top: 64px;
    z-index: 98;
    width: 100%;
  }
`

const AlphabetMenu: FC<AlphabetMenuProps> = ({
  activeAlphabet,
  onClick,
  disabledIndex,
  kind = 'default',
  loading,
  url = '',
  stickyProps,
}) => {
  const theme: ThemeType = useTheme()
  const [sticky, setSticky] = useState(false)

  const stickyToggle = () => {
    if (stickyProps) {
      const { indexClassName, startStickyClassName, stopStickyClassName, infiniteScroll } = stickyProps
      let positionToStopSticky = 0

      const indexHeight = indexClassName ? document.querySelector<HTMLElement>(indexClassName)?.offsetHeight || 0 : 0
      const positionToSticky = startStickyClassName
        ? document.querySelector<HTMLElement>(startStickyClassName)?.offsetHeight || 0
        : 0
      positionToStopSticky = stopStickyClassName
        ? document.querySelector<HTMLElement>(stopStickyClassName)?.offsetTop || 0
        : 0
      const alphabetMenuHeight = document.querySelector<HTMLElement>('.alphabet-menu')?.offsetHeight || 0

      if (!infiniteScroll) {
        positionToStopSticky -= alphabetMenuHeight
      }
      const windowScrollHeight = window.scrollY

      if (
        windowScrollHeight > positionToSticky &&
        windowScrollHeight < positionToStopSticky - 64 &&
        indexHeight > 607
      ) {
        !sticky && setSticky(true)
      } else {
        sticky && setSticky(false)
      }
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', stickyToggle)
    return () => {
      window.removeEventListener('scroll', stickyToggle)
    }
  }, [sticky, loading])

  return (
    <div
      className="alphabet-menu"
      css={[
        sticky && stickyProps && stickyAlphabetMenuStyle,
        kind === 'default' ? alphabetMenuStyle : alphabetMenuIndexStyle(theme),
      ]}
    >
      {loading ? (
        <Loader
          dataTestId="alphabet-menu-skeleton"
          className="alphabet-menu-skeleton"
          width="inherit"
          customSize={{
            sp: {
              width: kind === 'default' ? 360 : 44,
              height: kind === 'default' ? 24 : 607,
              borderRadius: 10,
            },
            pc: {
              width: 1190,
              height: 40,
              borderRadius: 10,
            },
          }}
        />
      ) : (
        <div className="alphabet-menu__wrapper">
          {ALPHABET.map((char, idx) => {
            if (kind === 'default') {
              return (
                <a
                  key={`char-${idx}`}
                  css={charStyle}
                  className={`${activeAlphabet === char ? 'active' : ''} ${
                    disabledIndex.includes(char) ? 'disabled' : ''
                  }`}
                  href={`${url}${encodeURIComponent(char.toLowerCase())}`}
                  onClick={(e) => disabledIndex.includes(char) && e.preventDefault()}
                >
                  {char}
                </a>
              )
            }
            return (
              <div
                key={`char-${idx}`}
                css={charIndexStyle(theme)}
                className={`${activeAlphabet === char ? 'active' : ''} ${
                  disabledIndex.includes(char) ? 'disabled' : ''
                }`}
                onClick={() => !disabledIndex.includes(char) && onClick(char)}
                onKeyDown={() => !disabledIndex.includes(char) && onClick(char)}
              >
                {char}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default AlphabetMenu
